/**
 * Role landing config — one file driving 6 public SEO landing pages.
 *
 * Each role page lives at /for/{slug} and is fully indexable. We surface
 * live counts from /api/stats so every page shows real numbers (Google
 * rewards freshness + accurate content), plus role-tailored value props,
 * FAQs (FAQPage schema), and one primary signup CTA that pre-fills the
 * register form with the right role.
 */

export const ROLE_LANDINGS = {
  buyers: {
    slug: 'buyers',
    role: 'buyer',
    eyebrow: 'For property buyers',
    h1: 'Buy property without paying agent commissions',
    sub:
      'Browse {properties}+ direct-from-seller listings in {countries} countries — UK, India, Canada. Get AI-instant matches, unlock a vetted local solicitor in 60 seconds, and pay one flat connection fee. No surprise add-ons.',
    cta_label: 'Start matching free',
    cta_link: '/auth?role=buyer',
    bullets: [
      'AI-instant matches every time you save a search',
      'Unlock direct contact for any listing — no agency relay',
      'Independent local agents, solicitors & brokers, all rated',
      'Same flat £15.52 / ₹1,535 / C$30 connection fee anywhere',
    ],
    why_us: [
      { h: 'Direct-from-seller listings', p: 'Every listing is harvested or verified — no double-agency markup.' },
      { h: 'AI shortlist in 60 seconds', p: 'Tell us your budget + city. We email a top-3 match the moment you sign up.' },
      { h: 'Trusted Pro network', p: 'Unlock the contact details of vetted estate agents, solicitors & brokers in your city.' },
    ],
    faqs: [
      { q: 'Does Shopperden charge buyers a commission?', a: 'No. We charge a flat connection fee (£15.52 in the UK, ₹1,535 in India, C$30 in Canada) per unlocked listing or pro contact. No percentage-of-price.' },
      { q: 'How do AI Instant Matches work?', a: 'When you sign up we email a personalised top-3 shortlist within 60 seconds based on the city, budget, and bedroom count you selected.' },
      { q: 'Are the local pros verified?', a: 'Yes — every estate agent, solicitor and broker on Shopperden is sourced from Google Places with their public rating & review count, plus a manual KYP claim flow.' },
      { q: 'Which countries do you cover?', a: 'United Kingdom, India and Canada today, with NRI-focused tooling for buyers researching from abroad.' },
    ],
  },
  sellers: {
    slug: 'sellers',
    role: 'seller',
    eyebrow: 'For property sellers',
    h1: 'Sell your property direct — no estate agent fees',
    sub:
      'List your home on Shopperden free. AI-match it instantly to {users}+ active buyers. Pick your own solicitor from the verified Pro directory. Optional £49 Pulse Investment Report for evidence-based pricing.',
    cta_label: 'List your property free',
    cta_link: '/auth?role=seller',
    bullets: [
      'No 1-2% agent commission — keep more of your sale',
      'Instant AI shortlist of qualified buyers in your city',
      '£49 Pulse Report for evidence-based pricing (optional)',
      'Pick your own solicitor from our Trusted Pros directory',
    ],
    why_us: [
      { h: 'Direct buyer matches', p: 'The moment you publish, our AI emails matched buyers a personalised top-3 — including yours.' },
      { h: 'Independent valuation', p: 'Optional £49 Pulse Report gives you median comps, 12-month outlook and AI commentary in 60 seconds.' },
      { h: 'Pick your own solicitor', p: 'Browse verified Google-rated conveyancing firms — pay only when you instruct them.' },
    ],
    faqs: [
      { q: 'Is listing on Shopperden really free?', a: 'Yes, listing is 100% free — you only pay if you order the optional £49 Pulse Investment Report or instruct a Verified Partner solicitor.' },
      { q: 'How long until I see buyer matches?', a: 'Within 60 seconds of publishing. We run AI Instant Match against every active buyer in your country.' },
      { q: 'Can I still use my own solicitor?', a: 'Absolutely. The Trusted Pros directory is optional — bring your own conveyancer if you prefer.' },
      { q: 'How do you compare to Rightmove or 99acres?', a: 'They charge agents £900-£1,500/month to list. We charge nothing for the listing — sellers and buyers pay only the flat connection fee when contact is unlocked.' },
    ],
  },
  tenants: {
    slug: 'tenants',
    role: 'tenant',
    eyebrow: 'For tenants & renters',
    h1: 'Rent direct from landlords — no admin fees',
    sub:
      'Browse to-let properties across UK, India and Canada. Unlock the landlord\'s direct number for £15.52 / ₹1,535 / C$30. No referencing rip-offs, no admin fee. Optional letting solicitor on tap.',
    cta_label: 'Find a rental',
    cta_link: '/auth?role=tenant',
    bullets: [
      'Direct landlord contact — no letting-agent middlemen',
      'Verified letting solicitors in {countries} countries',
      'Tenancy template + post-move-in companion built in',
      'Same flat connection fee anywhere on the platform',
    ],
    why_us: [
      { h: 'No admin fees', p: 'UK Tenant Fees Act compliant — Shopperden never charges referencing, holding, or admin fees.' },
      { h: 'Direct landlord access', p: 'Unlock the landlord\'s WhatsApp/phone for the same flat connection fee as buyers.' },
      { h: 'Free tenancy template', p: 'AST templates included — pay nothing for the paperwork unless you instruct a solicitor.' },
    ],
    faqs: [
      { q: 'Will I pay any letting-agent fees?', a: 'No. Shopperden bypasses letting agents entirely — you pay only the flat connection fee to reach the landlord.' },
      { q: 'Are landlords verified?', a: 'Yes, landlords sign a compliance declaration on listing, and tenants can rate them post-move.' },
      { q: 'Can I get a solicitor for the tenancy agreement?', a: 'Yes — browse local conveyancing solicitors in our Trusted Pros directory. Pay only when you instruct.' },
    ],
  },
  agents: {
    slug: 'agents',
    role: 'agent',
    eyebrow: 'For estate agents',
    h1: 'Generate verified leads — pay nothing upfront',
    sub:
      'List on Shopperden free. Get featured to {users}+ buyers in your area. Earn 20% commission on every referred unlock or Pulse Report. Your AI-powered listing studio is included.',
    cta_label: 'Join as an agent',
    cta_link: '/auth?role=agent',
    bullets: [
      'No £900/month subscription — pay only when you convert',
      '20% commission on referred buyers / sellers / unlocks',
      'AI listing copy + floor-plan generator included',
      'Verified Power Partner badge on your area\'s listings',
    ],
    why_us: [
      { h: 'Zero monthly fee', p: 'Unlike Rightmove or Zoopla, agent listings are free. We earn only when you earn.' },
      { h: 'AI listing studio', p: 'Auto-generate property descriptions, social posts, floor plans and brochures.' },
      { h: '20% lifetime commission', p: 'Every buyer or seller who signs up via your referral link earns you 20% of every unlock fee — forever.' },
    ],
    faqs: [
      { q: 'How much does it cost to list on Shopperden?', a: 'Listing is 100% free for verified agents. You only pay if you opt into the £49/month Verified Power Partner badge.' },
      { q: 'How do I get the 20% affiliate commission?', a: 'You receive a unique referral link on signup. Share it on social, with clients, or via WhatsApp — every signup pays you 20% of their unlock fees.' },
      { q: 'Can I list properties across countries?', a: 'Yes — UK, India and Canada are all supported with currency-aware pricing.' },
    ],
  },
  professionals: {
    slug: 'professionals',
    role: 'professional',
    eyebrow: 'For solicitors, brokers & property managers',
    h1: 'Get warm-intro leads, no advertising spend',
    sub:
      'Property buyers pay a £15.52 connection fee to reach you. We email you the warm intro before they call. Profile listing is free. Earn 20% commission on every referred client.',
    cta_label: 'Claim your Featured Pro profile',
    cta_link: '/auth?role=professional',
    bullets: [
      'Featured Pro directory in {countries} countries',
      'Warm-intro emails — every lead has paid to reach you',
      '20% commission on every referred unlock or instruction',
      'Estate agents · conveyancing solicitors · mortgage brokers · property managers',
    ],
    why_us: [
      { h: 'No advertising spend', p: 'Buyers pay to reach you — they\'re pre-qualified. We just route the warm intro.' },
      { h: 'Verified by Google', p: 'Your existing Google reviews appear on your Shopperden profile — instant credibility.' },
      { h: '20% commission', p: 'Refer a buyer/seller to Shopperden via your link and earn 20% of every fee they pay, forever.' },
    ],
    faqs: [
      { q: 'How much does it cost to be a Featured Pro?', a: 'Profile listing is 100% free. Optional Verified Power Partner badge is £49/month.' },
      { q: 'How do warm intros work?', a: 'When a buyer pays £15.52 / ₹1,535 / C$30 to unlock your contact, we email both of you the intro simultaneously — including the buyer\'s name and request.' },
      { q: 'What if I\'m already on Google?', a: 'You probably are — we may have already harvested your profile. Check by claiming via the link in your outreach email or search /pros/{your-city}.' },
    ],
  },
  nri: {
    slug: 'nri',
    role: 'buyer',
    eyebrow: 'For NRIs buying in India',
    h1: 'Buy property in India from London, Toronto or anywhere',
    sub:
      'Browse {properties}+ verified Indian listings. Get on-the-ground solicitors and property managers in 21 cities. Pay one flat ₹1,535 connection fee to unlock — no agency relay, no surprise add-ons.',
    cta_label: 'Browse India listings',
    cta_link: '/auth?role=buyer&intent=nri',
    bullets: [
      'Direct-from-developer + verified resale across India',
      'On-ground property managers in 21+ Indian cities',
      'NRI-aware solicitors for OCI/PAN compliance',
      'GBP / CAD / INR pricing — choose what suits',
    ],
    why_us: [
      { h: 'NRI-first tooling', p: 'OCI / PAN / FEMA-aware checklists and solicitor matches built into every flow.' },
      { h: '21+ Indian cities live', p: 'Mohali, Hyderabad, Delhi, Bangalore, Mumbai, Chennai, Pune — and growing every week.' },
      { h: 'On-ground partners', p: 'Verified property managers handle viewings, key handover and inspections while you\'re abroad.' },
    ],
    faqs: [
      { q: 'Can NRIs buy property on Shopperden?', a: 'Yes — every Indian listing is OCI/PAN-friendly, and our Trusted Pros directory includes NRI-aware solicitors and property managers.' },
      { q: 'Do I pay in GBP or INR?', a: 'Both. Our currency selector lets you browse in £, ₹ or C$. Stripe handles the FX automatically.' },
      { q: 'Who manages the property after I buy?', a: 'Property managers in our Featured Pro directory — handpicked per city. They do viewings, inspections, rent collection and key handover.' },
    ],
  },
};

export const ALL_ROLE_SLUGS = Object.keys(ROLE_LANDINGS);
